import {
    BCCCitySelection,
    BCCSelectionType,
    BCCSelectionLevel,
    IBCCCityList,
} from './common-types';
import {
    BCC_CUSTOM_ZONE_GROUPS,
    BCC_DEFAULT_FALLBACK,
} from './constants';

/*Generates a unique city ID from city data*/
export const getCityId = (city: IBCCCityList): string =>
    `${city.city_id}_${city.city_offtake}_${city.city_name}`;

/*Finds a city by its generated ID*/
export const findCityById = (
    citiesList: IBCCCityList[],
    cityId: string
): IBCCCityList | undefined =>
    citiesList.find((c) => getCityId(c) === cityId);

export interface CityDataStructure {
    [tier: string]: {
        [region: string]: IBCCCityList[];
    };
}

/*Builds a hierarchical city data structure (tier > region > cities)*/
export const buildCityDataStructure = (
    citiesList: IBCCCityList[]
): CityDataStructure => {
    const data: CityDataStructure = {};

    citiesList.forEach((city) => {
        const tier = city.tier || BCC_DEFAULT_FALLBACK;
        const region = city.region || BCC_DEFAULT_FALLBACK;

        if (!data[tier]) {
            data[tier] = {};
        }
        if (!data[tier][region]) {
            data[tier][region] = [];
        }
        data[tier][region].push(city);
    });

    return data;
};

/*Gets all unique regions from city data structure*/
export const getAllRegionsFromData = (cityData: CityDataStructure): string[] => {
    const regions = new Set<string>();
    Object.values(cityData).forEach((tierData) => {
        Object.keys(tierData).forEach((region) => regions.add(region));
    });
    return Array.from(regions);
};

/*Gets all tiers from city data structure*/
export const getAllTiersFromData = (cityData: CityDataStructure): string[] =>
    Object.keys(cityData);

/*Gets all zone names from predefined zone groups*/
export const getAllZoneNames = (): string[] =>
    BCC_CUSTOM_ZONE_GROUPS.map((group) => group.name);

/*Gets cities belonging to a zone group (handles combined zones)*/
export const getCitiesByZone = (
    citiesList: IBCCCityList[],
    zoneName: string
): IBCCCityList[] => {
    const group = BCC_CUSTOM_ZONE_GROUPS.find((g) => g.name === zoneName);
    if (group) {
        const zoneNames = group.zones as readonly string[];
        return citiesList.filter((city) => {
            const cityZone = city.zone || '';
            return zoneNames.includes(cityZone);
        });
    }
    // Fallback to exact zone match
    return citiesList.filter(
        (city) => (city.zone || BCC_DEFAULT_FALLBACK) === zoneName
    );
};

/*Expands a zone name to all city IDs within that zone*/
export const expandZoneToCityIds = (
    citiesList: IBCCCityList[],
    zoneName: string
): string[] => getCitiesByZone(citiesList, zoneName).map(getCityId);

/*Expands a region name to all city IDs within that region*/
export const expandRegionToCityIds = (
    cityData: CityDataStructure,
    regionName: string
): string[] => {
    const cityIds: string[] = [];
    Object.values(cityData).forEach((tierData) => {
        if (tierData[regionName]) {
            tierData[regionName].forEach((city) => {
                cityIds.push(getCityId(city));
            });
        }
    });
    return cityIds;
};

/*Expands a tier name to all city IDs within that tier*/
export const expandTierToCityIds = (
    cityData: CityDataStructure,
    tierName: string
): string[] => {
    const cityIds: string[] = [];
    if (cityData[tierName]) {
        Object.values(cityData[tierName]).forEach((cities) => {
            cities.forEach((city) => {
                cityIds.push(getCityId(city));
            });
        });
    }
    return cityIds;
};

interface ParsedSelectionParts {
    type: BCCSelectionType;
    level?: BCCSelectionLevel;
    label: string;
    cityIds: string[];
}

/*Parses a serialized selection string into its components*/
const parseSelectionString = (
    selectionStr: string,
    citiesList: IBCCCityList[]
): ParsedSelectionParts | null => {
    if (selectionStr.startsWith(`${BCCSelectionType.COHORT}:`)) {
        const parts = selectionStr.slice(7).split(':');
        if (parts.length >= 3) {
            return {
                type: BCCSelectionType.COHORT,
                level: parts[0] as BCCSelectionLevel,
                label: parts[1],
                cityIds: parts[2].split('|'),
            };
        }
        // Legacy format: cohort:id1|id2|id3
        const cohortItems = parts[0].split('|');
        const labels = cohortItems.map((id) => {
            const city = findCityById(citiesList, id);
            return city?.city_name || id;
        });
        return {
            type: BCCSelectionType.COHORT,
            level: BCCSelectionLevel.CITY,
            label: labels.join(' + '),
            cityIds: cohortItems,
        };
    }

    if (selectionStr.startsWith(`${BCCSelectionType.REGION}:`)) {
        const parts = selectionStr.slice(7).split(':');
        if (parts.length >= 2) {
            return {
                type: BCCSelectionType.REGION,
                label: parts[0],
                cityIds: parts[1].split('|'),
            };
        }
    }

    if (selectionStr.startsWith(`${BCCSelectionType.TIER}:`)) {
        const parts = selectionStr.slice(5).split(':');
        if (parts.length >= 2) {
            return {
                type: BCCSelectionType.TIER,
                label: parts[0],
                cityIds: parts[1].split('|'),
            };
        }
    }

    if (selectionStr.startsWith(`${BCCSelectionType.ZONE}:`)) {
        const parts = selectionStr.slice(5).split(':');
        if (parts.length >= 2) {
            return {
                type: BCCSelectionType.ZONE,
                label: parts[0],
                cityIds: parts[1].split('|'),
            };
        }
    }

    return null;
};

/*Parses serialized value array into BCCCitySelection array*/
export const parseSelectionsFromValue = (
    value: string[],
    citiesList: IBCCCityList[]
): BCCCitySelection[] => {
    const result: BCCCitySelection[] = [];

    (value || []).forEach((cityId) => {
        const parsed = parseSelectionString(cityId, citiesList);

        if (parsed) {
            result.push({
                id: Date.now() + Math.random(),
                type: parsed.type,
                cohortLevel: parsed.level,
                items: parsed.cityIds,
                label: parsed.label,
            });
        } else {
            // Regular city ID
            const city = findCityById(citiesList, cityId);
            if (city) {
                result.push({
                    id: Date.now() + Math.random(),
                    type: BCCSelectionType.CITY,
                    items: [cityId],
                    label: city.city_name,
                });
            }
        }
    });

    return result;
};

/*Serializes BCCCitySelection array to string array for storage*/
export const serializeSelectionsToValue = (
    selections: BCCCitySelection[]
): string[] => {
    const cityIds: string[] = [];

    selections.forEach((sel) => {
        if (sel.type === BCCSelectionType.COHORT) {
            // Format: cohort:level:displayLabel:cityId1|cityId2|cityId3
            cityIds.push(
                `${BCCSelectionType.COHORT}:${sel.cohortLevel || BCCSelectionLevel.CITY}:${sel.label}:${sel.items.join('|')}`
            );
        } else if (
            sel.type === BCCSelectionType.REGION ||
            sel.type === BCCSelectionType.TIER ||
            sel.type === BCCSelectionType.ZONE
        ) {
            // Format: type:displayLabel:cityId1|cityId2|cityId3
            cityIds.push(`${sel.type}:${sel.label}:${sel.items.join('|')}`);
        } else {
            cityIds.push(...sel.items);
        }
    });

    return cityIds;
};

/*Creates a unique selection ID*/
export const createSelectionId = (): number => Date.now() + Math.random();

/*Creates a new city selection object*/
export const createCitySelection = (
    city: IBCCCityList
): BCCCitySelection => ({
    id: createSelectionId(),
    type: BCCSelectionType.CITY,
    items: [getCityId(city)],
    label: city.city_name,
});

/*Creates a new region selection object*/
export const createRegionSelection = (
    regionName: string,
    cityIds: string[]
): BCCCitySelection => ({
    id: createSelectionId(),
    type: BCCSelectionType.REGION,
    items: cityIds,
    label: regionName,
});

/*Creates a new tier selection object*/
export const createTierSelection = (
    tierName: string,
    cityIds: string[]
): BCCCitySelection => ({
    id: createSelectionId(),
    type: BCCSelectionType.TIER,
    items: cityIds,
    label: tierName,
});

/*Creates a new zone selection object*/
export const createZoneSelection = (
    zoneName: string,
    cityIds: string[]
): BCCCitySelection => ({
    id: createSelectionId(),
    type: BCCSelectionType.ZONE,
    items: cityIds,
    label: zoneName,
});

/*Creates a new cohort selection object*/
export const createCohortSelection = (
    level: BCCSelectionLevel,
    items: string[],
    expandedCityIds: string[],
    label: string
): BCCCitySelection => ({
    id: createSelectionId(),
    type: BCCSelectionType.COHORT,
    cohortLevel: level,
    items: expandedCityIds,
    originalItems: [...items],
    label,
});

/*Checks if a city is selected as an individual city*/
export const isCitySelected = (
    selections: BCCCitySelection[],
    cityId: string
): boolean =>
    selections.some(
        (s) => s.type === BCCSelectionType.CITY && s.items.includes(cityId)
    );

/*Checks if a city is in a city-level cohort*/
export const isCityInCohort = (
    selections: BCCCitySelection[],
    cityId: string
): boolean =>
    selections.some(
        (s) =>
            s.type === BCCSelectionType.COHORT &&
            s.cohortLevel === BCCSelectionLevel.CITY &&
            s.originalItems?.includes(cityId)
    );

/*Checks if a region is selected as an individual region*/
export const isRegionSelected = (
    selections: BCCCitySelection[],
    regionName: string
): boolean =>
    selections.some(
        (s) => s.type === BCCSelectionType.REGION && s.label === regionName
    );

/*Checks if a region is in a region-level cohort*/
export const isRegionInCohort = (
    selections: BCCCitySelection[],
    regionName: string
): boolean =>
    selections.some(
        (s) =>
            s.type === BCCSelectionType.COHORT &&
            s.cohortLevel === BCCSelectionLevel.REGION &&
            s.originalItems?.includes(regionName)
    );

/*Checks if a tier is selected as an individual tier*/
export const isTierSelected = (
    selections: BCCCitySelection[],
    tierName: string
): boolean =>
    selections.some(
        (s) => s.type === BCCSelectionType.TIER && s.label === tierName
    );

/*Checks if a tier is in a tier-level cohort*/
export const isTierInCohort = (
    selections: BCCCitySelection[],
    tierName: string
): boolean =>
    selections.some(
        (s) =>
            s.type === BCCSelectionType.COHORT &&
            s.cohortLevel === BCCSelectionLevel.TIER &&
            s.originalItems?.includes(tierName)
    );

/*Checks if a zone is selected as an individual zone*/
export const isZoneSelected = (
    selections: BCCCitySelection[],
    zoneName: string
): boolean =>
    selections.some(
        (s) => s.type === BCCSelectionType.ZONE && s.label === zoneName
    );

/*Checks if a zone is in a zone-level cohort*/
export const isZoneInCohort = (
    selections: BCCCitySelection[],
    zoneName: string
): boolean =>
    selections.some(
        (s) =>
            s.type === BCCSelectionType.COHORT &&
            s.cohortLevel === BCCSelectionLevel.CUSTOM &&
            s.originalItems?.includes(zoneName)
    );

/*Checks if a city is part of a zone-level selection*/
export const isCityInZoneSelection = (
    selections: BCCCitySelection[],
    cityId: string
): boolean =>
    selections.some(
        (s) => s.type === BCCSelectionType.ZONE && s.items.includes(cityId)
    );

/*Generic check if an item is in any cohort at the specified level*/
export const isInAnyCohort = (
    selections: BCCCitySelection[],
    itemId: string,
    level: BCCSelectionLevel
): boolean => {
    const levelMapping: Record<BCCSelectionLevel, BCCSelectionLevel> = {
        [BCCSelectionLevel.CITY]: BCCSelectionLevel.CITY,
        [BCCSelectionLevel.REGION]: BCCSelectionLevel.REGION,
        [BCCSelectionLevel.TIER]: BCCSelectionLevel.TIER,
        [BCCSelectionLevel.CUSTOM]: BCCSelectionLevel.CUSTOM,
    };

    return selections.some(
        (s) =>
            s.type === BCCSelectionType.COHORT &&
            s.cohortLevel === levelMapping[level] &&
            s.originalItems?.includes(itemId)
    );
};

/*Filters items by search query*/
export const filterBySearch = <T>(
    items: T[],
    searchQuery: string,
    getLabel: (item: T) => string
): T[] => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter((item) => getLabel(item).toLowerCase().includes(query));
};

/*Filters cities by search query*/
export const filterCitiesBySearch = (
    cities: IBCCCityList[],
    searchQuery: string
): IBCCCityList[] =>
    filterBySearch(cities, searchQuery, (city) => city.city_name);

/*Filters string items by search query*/
export const filterStringsBySearch = (
    items: string[],
    searchQuery: string
): string[] => filterBySearch(items, searchQuery, (item) => item);

/*Gets tooltip content for a selection*/
export const getSelectionTooltip = (
    selection: BCCCitySelection,
    citiesList: IBCCCityList[]
): string => {
    if (selection.type === BCCSelectionType.COHORT) {
        const items = selection.originalItems || selection.items;
        if (selection.cohortLevel === BCCSelectionLevel.CITY) {
            return items
                .map((id) => {
                    const city = findCityById(citiesList, id);
                    return city?.city_name || id;
                })
                .join(', ');
        }
        return items.join(', ');
    }
    return '';
};

/*Generates a compact display label for cohorts*/
export const generateCohortLabel = (
    items: string[],
    level: BCCSelectionLevel,
    citiesList: IBCCCityList[]
): string => {
    const labels = items.map((id) => {
        if (level === BCCSelectionLevel.CITY) {
            const city = findCityById(citiesList, id);
            return city?.city_name || id;
        }
        return id;
    });

    return labels.length > 1 ? `${labels[0]} +${labels.length - 1}` : labels[0];
};

/*Expands cohort items to city IDs based on level*/
export const expandCohortItemsToCityIds = (
    items: string[],
    level: BCCSelectionLevel,
    cityData: CityDataStructure,
    citiesList: IBCCCityList[]
): string[] => {
    let expandedCityIds: string[] = [];

    switch (level) {
        case BCCSelectionLevel.CITY:
            expandedCityIds = [...items];
            break;
        case BCCSelectionLevel.REGION:
            items.forEach((regionName) => {
                expandedCityIds.push(...expandRegionToCityIds(cityData, regionName));
            });
            break;
        case BCCSelectionLevel.TIER:
            items.forEach((tierName) => {
                expandedCityIds.push(...expandTierToCityIds(cityData, tierName));
            });
            break;
        case BCCSelectionLevel.CUSTOM:
            items.forEach((zoneName) => {
                expandedCityIds.push(...expandZoneToCityIds(citiesList, zoneName));
            });
            break;
    }

    return [...new Set(expandedCityIds)]; // Remove duplicates
};

// Get search placeholder based on level
export const getSearchPlaceholder = (level: BCCSelectionLevel, selectedZone: string | null): string => {
    switch (level) {
        case BCCSelectionLevel.CITY: return 'Search cities...';
        case BCCSelectionLevel.REGION: return 'Search regions...';
        case BCCSelectionLevel.TIER: return 'Search tiers...';
        case BCCSelectionLevel.CUSTOM: return selectedZone ? 'Search cities...' : 'Search zones...';
        default: return 'Search...';
    }
};